import { useState, useEffect, useMemo } from "react";
import { fetchOurApi } from "../../../../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../../../../helpers/accountHelpers";
import { useMutateCachedCollection } from "../../../../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../../../../helpers/components/vendors";

const callApi = async (path, data) => {
  const response = await fetchOurApi({ path, method: "POST", data });

  if (!response) {
    throw new Error(`No response from ${path}`);
  }
  if (response.error) {
    const message =
      typeof response.error === "string"
        ? response.error
        : response.message || `API error from ${path}`;
    const err = new Error(message);
    // duplicateVendor.js's apply() can reject with { industriesDiffer: true }
    // specifically (not just any error) when the issue's industries diverge
    // and the caller hasn't acknowledged it -- callers need to tell this
    // apart from a generic failure to recover without a full page reload.
    if (response.industriesDiffer) {
      err.industriesDiffer = true;
    }
    throw err;
  }
  return response;
};

const SECTION_LABELS = ["cleanup issues", "empty vendors"];

const buildPartialFailureMessage = (results) => {
  const failedLabels = results
    .map((r, i) => (r.status === "rejected" ? SECTION_LABELS[i] : null))
    .filter(Boolean);
  return failedLabels.length > 0
    ? `Could not load: ${failedLabels.join(", ")}`
    : null;
};

// A duplicate-vendor issue's payload already matches what DuplicateClusterSlider/
// ClusterRow/ZeroTxnClusterSlider expect (vendors/confidence/matchBasis/
// recommendedPrimaryId/industriesDiffer/isZeroTxnPair) -- clusterId is just the
// issue's own doc id, replacing what buildEnrichedCluster used to compute.
const toCluster = (issue) => ({ ...issue.payload, clusterId: issue.id, status: "pending" });

// low-confidence-naics and government-entity-misclassification issues both
// render through NaicsReviewSlider/NaicsRow. low-confidence-naics already
// carries a current-vs-proposed shape; government-entity-misclassification
// only has a current classification plus a fixed zero-out fix (it declares no
// overrideFields -- there's no alternate classification to propose), so it's
// adapted onto the same current/proposed shape with that fixed fix as the
// "proposed" side.
const toNaicsIssueFromLowConfidence = (issue) => {
  const { payload } = issue;
  return {
    issueId: issue.id,
    relationshipId: payload.relationshipId,
    vendorId: payload.vendorId,
    name: payload.vendorName,
    vendorName: payload.vendorName,
    naicsTitle: payload.currentNaicsTitle,
    emissionCategory: payload.currentEmissionCategory,
    confidenceScore: payload.confidenceScore,
    transactionCount: payload.transactionCount,
    dollarAmount: payload.dollarAmount,
    reason: payload.reason,
    referenceNaicsTitle: payload.naicsTitle,
    referenceEmissionCategory: payload.emissionCategory,
    status: "pending"
  };
};

const toNaicsIssueFromGovernmentEntity = (issue) => {
  const { payload } = issue;
  return {
    issueId: issue.id,
    relationshipId: payload.relationshipId,
    vendorId: payload.vendorId,
    name: payload.vendorName,
    vendorName: payload.vendorName,
    naicsTitle: payload.currentNaicsTitle,
    emissionCategory: payload.currentEmissionCategory,
    confidenceScore: payload.llmConfidence,
    reason: "government-entity",
    referenceNaicsTitle: null,
    referenceEmissionCategory: "spend-based",
    status: "pending"
  };
};

const NAICS_ADAPTERS_BY_ISSUE_TYPE = {
  "low-confidence-naics": toNaicsIssueFromLowConfidence,
  "government-entity-misclassification": toNaicsIssueFromGovernmentEntity
};

export const useCleanupCandidates = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [naicsIssues, setNaicsIssues] = useState([]);
  const [emptyVendors, setEmptyVendors] = useState([]);
  const [zeroTxnPairs, setZeroTxnPairs] = useState([]);
  const [error, setError] = useState(null);
  // Kept separate from `error` (rather than a shared setter both loaders
  // call) so loadCandidates and loadZeroTxnPairs can never race to decide
  // which message wins -- loadCandidates's error always takes priority,
  // regardless of which of the two independent fetches below settles first.
  const [zeroTxnError, setZeroTxnError] = useState(null);
  const [mergingClusterIds, setMergingClusterIds] = useState([]);

  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();
  const mutateCachedCollection = useMutateCachedCollection();

  const invalidateVendorCaches = () => {
    mutateCachedCollection("vendor-relationships");
    mutateCachedCollection("transactions");
  };

  useEffect(() => {
    let isMounted = true;

    // get-zero-txn-duplicate-pairs is a full relationship scan -- it's left
    // out of the initial Promise.allSettled and fetched separately below so
    // the bulk "Archive all empty-duplicates" banner pops in once ready
    // instead of blocking the rest of the page behind it.
    const loadCandidates = async () => {
      const companyId = getAccountId();

      const results = await Promise.allSettled([
        callApi("/vendors/get-vendor-cleanup-issues", { companyId }),
        callApi("/vendors/detect-empty-vendors", { companyId })
      ]);

      if (!isMounted) {
        return;
      }

      const [issuesResult, emptyResult] = results;

      if (issuesResult.status === "fulfilled") {
        const issues = issuesResult.value?.issues || [];
        setClusters(
          issues.filter((issue) => issue.issueType === "duplicate-vendor").map(toCluster)
        );
        setNaicsIssues(
          issues
            .filter((issue) => NAICS_ADAPTERS_BY_ISSUE_TYPE[issue.issueType])
            .map((issue) => NAICS_ADAPTERS_BY_ISSUE_TYPE[issue.issueType](issue))
        );
      }
      if (emptyResult.status === "fulfilled") {
        const next = emptyResult.value?.emptyVendors || [];
        setEmptyVendors(next.map((v) => ({ ...v, status: "pending" })));
      }

      const allFailed = results.every(({ status }) => status === "rejected");
      if (allFailed) {
        setError("Failed to load cleanup candidates");
      } else {
        const partialMessage = buildPartialFailureMessage(results);
        if (partialMessage) {
          setError(partialMessage);
        }
      }

      setIsLoading(false);
    };

    const loadZeroTxnPairs = async () => {
      try {
        const response = await callApi("/vendors/get-zero-txn-duplicate-pairs", {
          companyId: getAccountId()
        });
        if (!isMounted) {
          return;
        }
        const next = response?.zeroTxnPairs || [];
        setZeroTxnPairs(next.map((p) => ({ ...p, status: "pending" })));
      } catch (err) {
        if (isMounted) {
          setZeroTxnError("Could not load: empty-duplicate vendors");
        }
      }
    };

    loadCandidates();
    loadZeroTxnPairs();

    return () => {
      isMounted = false;
    };
  }, []);

  const mergeCluster = async ({ clusterId, primaryId, naicsOverride, acknowledgedIndustryDifference }) => {
    const companyId = getAccountId();

    // The slider that triggers this closes immediately on click, well before
    // this request resolves -- while it's in flight the cluster still shows
    // status:"pending" and can be reopened, letting a second merge request
    // fire for the same cluster before the first one lands. This is the
    // authoritative guard (not just a UI disable) -- it holds even if two
    // calls somehow get triggered in the same tick. The actual race this
    // guards against (PR #2025) is now resolved server-side too, inside
    // resolveVendorCleanupIssue's claim/revalidate step, but there's no
    // reason to rely on that alone when the client already knows better.
    if (mergingClusterIds.includes(clusterId)) {
      return;
    }

    setMergingClusterIds((current) => [...current, clusterId]);

    try {
      await callApi("/vendors/accept-vendor-cleanup-issue", {
        companyId,
        issueId: clusterId,
        override: {
          recommendedPrimaryId: primaryId,
          ...(naicsOverride ? { naicsOverride } : {}),
          ...(acknowledgedIndustryDifference ? { acknowledgedIndustryDifference: true } : {})
        }
      });

      setClusters((current) =>
        current.map((c) =>
          c.clusterId === clusterId ? { ...c, status: "merged" } : c
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      // The slider already closed by the time this rejection comes back
      // (it closes on click, not on response -- see its own comment above).
      // A plain error message would leave the buyer with no way to retry
      // except reloading the whole page to see the checkbox the slider
      // would otherwise show them. Patching industriesDiffer onto the local
      // cluster lets the same slider reopen with that checkbox available,
      // even if the pair was shown to the buyer before this diverged.
      if (err.industriesDiffer) {
        setClusters((current) =>
          current.map((c) =>
            c.clusterId === clusterId ? { ...c, industriesDiffer: true } : c
          )
        );
      }
      setError(err.message || "Failed to merge cluster");
    } finally {
      setMergingClusterIds((current) => current.filter((id) => id !== clusterId));
    }
  };

  // Archives (never merges) the zero-transaction side of an isZeroTxnPair
  // cluster. The detector's own apply() already knows which side is empty
  // (payload.recommendedPrimaryId) and re-verifies it's still empty before
  // writing, so this is just a plain accept with no override needed. Shares
  // mergingClusterIds with mergeCluster so the same in-flight guard covers
  // both actions.
  const confirmZeroTxnCluster = async (cluster) => {
    const { clusterId } = cluster;

    if (mergingClusterIds.includes(clusterId)) {
      return;
    }

    setMergingClusterIds((current) => [...current, clusterId]);

    try {
      await callApi("/vendors/accept-vendor-cleanup-issue", {
        companyId: getAccountId(),
        issueId: clusterId
      });

      setClusters((current) =>
        current.map((c) =>
          c.clusterId === clusterId ? { ...c, status: "archived" } : c
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      setError(err.message || "Failed to archive empty-duplicate vendor");
    } finally {
      setMergingClusterIds((current) => current.filter((id) => id !== clusterId));
    }
  };

  const dismissCluster = async (clusterId) => {
    try {
      await callApi("/vendors/dismiss-vendor-cleanup-issue", {
        companyId: getAccountId(),
        issueId: clusterId
      });

      setClusters((current) =>
        current.map((c) =>
          c.clusterId === clusterId ? { ...c, status: "dismissed" } : c
        )
      );
    } catch (err) {
      setError(err.message || "Failed to dismiss cluster");
    }
  };

  const archiveZeroTxnPairs = async (pairIds) => {
    if (!pairIds?.length) {
      return;
    }
    try {
      await callApi("/vendors/archive-zero-txn-pairs", {
        companyId: getAccountId(),
        pairIds
      });

      setZeroTxnPairs((current) =>
        current.map((p) =>
          pairIds.includes(p.pairId) ? { ...p, status: "archived" } : p
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      setError(err.message || "Failed to archive empty-duplicate vendors");
    }
  };

  // Clears only whichever of the two actually surfaced (error always wins
  // the merged `error || zeroTxnError` the view shows) -- clearing both
  // unconditionally would silently drop a zeroTxnError that happened to
  // fail in the same window as error, since the view only ever shows one
  // message per clearError call. Clearing just the surfaced one lets the
  // merged value fall through to the other on the next render instead.
  const clearError = () => {
    if (error) {
      setError(null);
    } else {
      setZeroTxnError(null);
    }
  };

  // override is only sent for the "search a different industry" flow --
  // accepting a low-confidence classification as-is, a merge-conflict's
  // reference, or a government-entity fix all just accept the issue's own
  // proposed fix unchanged, which the backend applies with no override at
  // all. government-entity-misclassification in particular declares no
  // overrideFields, so sending one there would be rejected outright.
  const updateNaics = async ({ issueId, override }) => {
    try {
      await callApi("/vendors/accept-vendor-cleanup-issue", {
        companyId: getAccountId(),
        issueId,
        ...(override ? { override } : {})
      });

      setNaicsIssues((current) =>
        current.map((v) =>
          v.issueId === issueId ? { ...v, status: "updated" } : v
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      setError(err.message || "Failed to update industry");
    }
  };

  const dismissNaics = async (issueId) => {
    try {
      await callApi("/vendors/dismiss-vendor-cleanup-issue", {
        companyId: getAccountId(),
        issueId
      });

      setNaicsIssues((current) =>
        current.map((v) =>
          v.issueId === issueId ? { ...v, status: "dismissed" } : v
        )
      );
    } catch (err) {
      setError(err.message || "Failed to dismiss industry review");
    }
  };

  const archiveEmptyVendor = async (relationshipId) => {
    try {
      await updateVendorRelationshipFields(relationshipId, {
        archived: true
      });

      setEmptyVendors((current) =>
        current.map((v) =>
          v.relationshipId === relationshipId
            ? { ...v, status: "archived" }
            : v
        )
      );
    } catch (err) {
      setError(err.message || "Failed to archive vendor");
    }
  };

  const counts = useMemo(
    () => ({
      duplicateClusterCount: clusters.filter(
        ({ status }) => status === "pending"
      ).length,
      naicsIssueCount: naicsIssues.filter(
        ({ status }) => status === "pending"
      ).length,
      emptyVendorCount: emptyVendors.filter(
        ({ status }) => status === "pending"
      ).length,
      zeroTxnPairCount: zeroTxnPairs.filter(
        ({ status }) => status === "pending"
      ).length,
      totalVendorsFlagged:
        clusters
          .filter(({ status }) => status === "pending")
          .reduce((sum, { vendors }) => sum + vendors.length, 0) +
        naicsIssues.filter(({ status }) => status === "pending").length +
        emptyVendors.filter(({ status }) => status === "pending").length
    }),
    [clusters, naicsIssues, emptyVendors, zeroTxnPairs]
  );

  return {
    isLoading,
    error: error || zeroTxnError,
    clearError,
    data: {
      duplicateClusters: clusters,
      naicsIssues,
      emptyVendors,
      zeroTxnPairs
    },
    counts,
    mergeCluster,
    mergingClusterIds,
    confirmZeroTxnCluster,
    dismissCluster,
    updateNaics,
    dismissNaics,
    archiveEmptyVendor,
    archiveZeroTxnPairs
  };
};
